import type { FeatureKey } from '@/types/platform';

/**
 * The site's primary destinations, in one place.
 *
 * These were declared inside Header, which was fine while the header was the
 * only thing that navigated. It is not: the footer needs them, and below `lg`
 * the header's bar is hidden altogether, so the mobile menu needs them too.
 * Three copies of a list like this drift — a page gets added to one and is
 * quietly unreachable from the others — so it lives here and every consumer
 * filters the same array.
 *
 * `feature` gates an entry on a platform flag. A destination whose feature is
 * off is REMOVED rather than shown inert, so navigation never offers a link
 * to a section that will refuse to render (§86).
 */
export interface NavLinkItem {
  to: string;
  label: string;
  feature?: FeatureKey;
}

export const PRIMARY_LINKS: NavLinkItem[] = [
  { to: '/shop', label: 'Shop', feature: 'store' },
  { to: '/occasions', label: 'Occasions', feature: 'store' },
  { to: '/personalised-gifts', label: 'Personalised Gifts', feature: 'personalisation' },
  { to: '/luxury-gift-boxes', label: 'Luxury Gift Boxes', feature: 'store' },
  { to: '/bespoke-gifting', label: 'Bespoke Gifting', feature: 'bespoke_gifting' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

/**
 * Account-area destinations, offered in the mobile menu.
 *
 * Kept separate from the primary list because they are personal rather than
 * navigational, and the two are grouped apart wherever both are shown.
 */
export const ACCOUNT_LINKS: NavLinkItem[] = [
  { to: '/account', label: 'My account', feature: 'customer_accounts' },
  { to: '/account/orders', label: 'My orders', feature: 'customer_accounts' },
  { to: '/wishlist', label: 'Wishlist', feature: 'wishlist' },
];

/**
 * Drop the entries whose feature is switched off.
 *
 * The header, the footer and the mobile menu each wrote this same predicate
 * inline. Beyond the duplication, the narrowing of `feature?: FeatureKey`
 * through `!link.feature || isVisible(link.feature)` is context-sensitive and
 * did not hold in every call site, so one of the three failed to compile
 * while the other two passed on identical code. An explicit guard is both
 * shared and unambiguous.
 */
export function visibleLinks(
  links: ReadonlyArray<NavLinkItem>,
  isVisible: (feature: FeatureKey) => boolean,
): NavLinkItem[] {
  return links.filter((link) => {
    if (link.feature === undefined) return true;

    return isVisible(link.feature);
  });
}
